import { tv } from 'tailwind-variants'
import { z } from 'zod'

import { body2 } from '@nxt-insurance/ui-kit/core/typography'

import type { PortableTextTypeComponentProps } from '@portabletext/react'

const lineListStyles = tv({
  slots: {
    container: 'flex flex-col gap-3 pb-5',
    separator: 'border-t border-grey-200',
    row: 'flex flex-row items-start justify-between gap-4',
    label: body2({ color: 'grey700' }),
    value: body2({ align: 'right', className: 'shrink-0' }),
  },
  variants: {
    lineType: {
      dashed: { separator: 'border-dashed' },
      solid: { separator: 'border-solid' },
    },
    boldValue: {
      true: { value: body2({ align: 'right', bold: true }) },
    },
  },
})

const separatorItem = z.object({
  _key: z.string(),
  type: z.literal('separator'),
  lineType: z.enum(['dashed', 'solid']).optional(),
})

const lineItem = z.object({
  _key: z.string(),
  label: z.string(),
  value: z.string().optional(),
  config: z.object({ boldValue: z.boolean().optional() }).optional(),
})

const lineList = z.object({
  _type: z.literal('lineList'),
  data: z.array(z.union([separatorItem, lineItem])).default([]),
})

export const LineList = (props: { node: PortableTextTypeComponentProps<unknown> }) => {
  const parsed = lineList.safeParse(props.node.value)

  if (!parsed.success) {
    return null
  }

  const { container, separator, row, label, value } = lineListStyles()

  return (
    <div className={container()}>
      {parsed.data.data.map((item) =>
        'type' in item ? (
          <hr
            key={item._key}
            className={separator({ lineType: item.lineType === 'dashed' ? 'dashed' : 'solid' })}
          />
        ) : (
          <div key={item._key} className={row()}>
            <span className={label()}>{item.label}</span>
            {item.value ? (
              <span className={value({ boldValue: item.config?.boldValue === true })}>
                {item.value}
              </span>
            ) : null}
          </div>
        )
      )}
    </div>
  )
}
